import React, { useEffect, useState } from 'react';
import { Pause, Pencil, Play, Square, X } from 'lucide-react';
import api from '../api/api';
import { Message, Confirm, Modal, SearchableSelect, TableSkeleton, EmptyState } from '../components';
import { PageHeader } from '../components/layout';

const queryOperatorOptions = [
  { value: 'equals', label: '等于' },
  { value: 'not_equals', label: '不等于' },
  { value: 'contains', label: '包含' },
  { value: 'not_contains', label: '不包含' },
  { value: 'in', label: '属于' },
  { value: 'not_in', label: '不属于' },
  { value: 'is_empty', label: '为空' },
  { value: 'is_not_empty', label: '不为空' },
  { value: 'gt', label: '大于' },
  { value: 'lt', label: '小于' },
  { value: 'gte', label: '大于等于' },
  { value: 'lte', label: '小于等于' }
];

const weekDayOptions = [
  { value: 0, label: '周一' },
  { value: 1, label: '周二' },
  { value: 2, label: '周三' },
  { value: 3, label: '周四' },
  { value: 4, label: '周五' },
  { value: 5, label: '周六' },
  { value: 6, label: '周日' }
];

const scheduleLabel = (task) => {
  if (task.schedule_type === 'hourly') return `每 ${task.interval_minutes || 60} 分钟`;
  if (task.schedule_type === 'weekly') {
    const weekDay = weekDayOptions.find(option => option.value === task.week_day)?.label || '周一';
    return `每周 ${weekDay} ${task.schedule_time || '09:00'}`;
  }
  return `每天 ${task.schedule_time || '09:00'}`;
};

const createEmptyEditForm = () => ({
  name: '',
  group_id: '',
  condition_logic: 'and',
  max_results: 100,
  page_size: 20,
  is_active: true,
  schedule_type: 'hourly',
  interval_minutes: 60,
  schedule_time: '09:00',
  week_day: 0,
  dedupe_hours: 24
});

const QueryTriggerTasksPage = ({ canCancelQueryTriggers = false }) => {
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmState, setDeleteConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });
  const [cancelConfirmState, setCancelConfirmState] = useState({ isOpen: false, task: null });
  const [cancellingTaskId, setCancellingTaskId] = useState(null);
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState(createEmptyEditForm());
  const [editConditions, setEditConditions] = useState([]);
  const [newEditCondition, setNewEditCondition] = useState({ field: '', operator: 'equals', value: '' });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksResponse, groupsResponse, fieldsResponse] = await Promise.all([
        api.getQueryTriggerTasks(),
        api.getGroups(),
        api.getFields()
      ]);
      setTasks(Array.isArray(tasksResponse.data) ? tasksResponse.data : []);
      setGroups(Array.isArray(groupsResponse.data) ? groupsResponse.data : []);
      setFields(Array.isArray(fieldsResponse.data) ? fieldsResponse.data : []);
    } catch (err) {
      setAlertState({ isOpen: true, message: '加载定时任务失败: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getGroupName = (groupId) => groups.find(group => group.id === groupId)?.name || `规则组 ${groupId}`;
  const getFieldName = (fieldId) => fields.find(field => field.field_id === fieldId)?.display_name || fieldId;
  const getOperatorLabel = (operator) => queryOperatorOptions.find(option => option.value === operator)?.label || operator;
  const selectedEditField = fields.find(field => field.field_id === newEditCondition.field);
  const selectedEditFieldOptions = selectedEditField?.options || [];
  const editConditionNeedsValue = !['is_empty', 'is_not_empty'].includes(newEditCondition.operator);
  const editConditionSupportsMultipleValues = ['contains', 'not_contains', 'in', 'not_in'].includes(newEditCondition.operator) && selectedEditFieldOptions.length > 0;

  const conditionSummary = (task) => {
    const conditions = task.conditions || [];
    if (conditions.length === 0) return '-';
    const logic = task.condition_logic === 'or' ? '任一' : '全部';
    return `${conditions.length} 个条件（${logic}）：` + conditions
      .slice(0, 3)
      .map(condition => `${getFieldName(condition.field)} ${getOperatorLabel(condition.operator)}${condition.value ? ` ${Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}` : ''}`)
      .join('，');
  };

  const getStatusBadge = (active) => active
    ? <span className="badge badge-success">启用</span>
    : <span className="badge badge-default">停用</span>;

  const getRunStatusBadge = (runStatus) => {
    if (runStatus === 'running') {
      return <span className="badge badge-info">执行中</span>;
    }
    if (runStatus === 'cancel_requested') {
      return <span className="badge badge-warning">终止中</span>;
    }
    return <span className="badge badge-default">空闲</span>;
  };

  const handleToggleTask = async (task) => {
    try {
      await api.updateQueryTriggerTask(task.id, { is_active: !task.is_active });
      await fetchData();
    } catch (err) {
      setAlertState({ isOpen: true, message: '更新失败: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const handleOpenEditTask = (task) => {
    setEditingTask(task);
    setEditForm({
      name: task.name || '',
      group_id: task.group_id || '',
      condition_logic: task.condition_logic || 'and',
      max_results: task.max_results || 100,
      page_size: task.page_size || 20,
      is_active: task.is_active !== false,
      schedule_type: task.schedule_type || 'hourly',
      interval_minutes: task.interval_minutes || 60,
      schedule_time: task.schedule_time || '09:00',
      week_day: task.week_day ?? 0,
      dedupe_hours: task.dedupe_hours ?? 24
    });
    setEditConditions((task.conditions || []).map(condition => ({ ...condition })));
    setNewEditCondition({ field: '', operator: 'equals', value: '' });
  };

  const handleCloseEditTask = () => {
    setEditingTask(null);
    setEditForm(createEmptyEditForm());
    setEditConditions([]);
    setNewEditCondition({ field: '', operator: 'equals', value: '' });
  };

  const handleEditFormChange = (key, value) => {
    setEditForm(prev => ({ ...prev, [key]: value }));
  };

  const addEditCondition = () => {
    if (!newEditCondition.field) {
      setAlertState({ isOpen: true, message: '请选择查询字段' });
      return;
    }
    if (editConditionNeedsValue && Array.isArray(newEditCondition.value) && newEditCondition.value.length === 0) {
      setAlertState({ isOpen: true, message: '请选择查询条件值' });
      return;
    }
    if (editConditionNeedsValue && !Array.isArray(newEditCondition.value) && !String(newEditCondition.value || '').trim()) {
      setAlertState({ isOpen: true, message: '请输入查询条件值' });
      return;
    }
    setEditConditions(prev => [...prev, { ...newEditCondition }]);
    setNewEditCondition({ field: '', operator: 'equals', value: '' });
  };

  const removeEditCondition = (index) => {
    setEditConditions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveEditTask = async () => {
    if (!editingTask) return;
    if (!String(editForm.name || '').trim()) {
      setAlertState({ isOpen: true, message: '请输入定时任务名称' });
      return;
    }
    if (!editForm.group_id) {
      setAlertState({ isOpen: true, message: '请选择规则组' });
      return;
    }
    if (editConditions.length === 0) {
      setAlertState({ isOpen: true, message: '请至少添加一个查询条件' });
      return;
    }
    const maxResults = Number(editForm.max_results);
    const pageSize = Number(editForm.page_size);
    if (!Number.isInteger(maxResults) || maxResults < 1 || maxResults > 10000) {
      setAlertState({ isOpen: true, type: 'error', message: '最大数量必须为 1–10000 的整数' });
      return;
    }
    if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > 50) {
      setAlertState({ isOpen: true, type: 'error', message: '单页数量必须为 1–50 的整数' });
      return;
    }
    if (pageSize > maxResults) {
      setAlertState({ isOpen: true, message: '单页数量不能大于最大数量' });
      return;
    }

    try {
      await api.updateQueryTriggerTask(editingTask.id, {
        name: editForm.name.trim(),
        group_id: parseInt(editForm.group_id, 10),
        condition_logic: editForm.condition_logic,
        conditions: editConditions,
        max_results: maxResults,
        page_size: pageSize,
        is_active: editForm.is_active,
        schedule_type: editForm.schedule_type,
        interval_minutes: parseInt(editForm.interval_minutes, 10) || 60,
        schedule_time: editForm.schedule_time || '09:00',
        week_day: parseInt(editForm.week_day, 10) || 0,
        dedupe_hours: parseInt(editForm.dedupe_hours, 10) || 24
      });
      handleCloseEditTask();
      await fetchData();
      setAlertState({ isOpen: true, message: '定时任务已更新' });
    } catch (err) {
      setAlertState({ isOpen: true, message: '保存失败: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const handleDeleteTask = async (task) => {
    setDeleteConfirmState({
      isOpen: true,
      message: `确定要删除定时任务「${task.name}」吗？`,
      onConfirm: async () => {
        try {
          await api.deleteQueryTriggerTask(task.id);
          await fetchData();
        } catch (err) {
          setAlertState({ isOpen: true, message: '删除失败: ' + (err.response?.data?.detail || err.message) });
        }
        setDeleteConfirmState({ isOpen: false, message: '', onConfirm: null });
      }
    });
  };

  const handleCancelRunningTask = (task) => {
    setCancelConfirmState({ isOpen: true, task });
  };

  const confirmCancelRunningTask = async () => {
    const task = cancelConfirmState.task;
    if (!task || cancellingTaskId) return;
    setCancellingTaskId(task.id);
    try {
      const response = await api.cancelRunningQueryTriggerTask(task.id);
      setCancelConfirmState({ isOpen: false, task: null });
      setAlertState({ isOpen: true, message: response.data.message });
      await fetchData();
    } catch (err) {
      setAlertState({ isOpen: true, message: '终止失败: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setCancellingTaskId(null);
    }
  };

  return (
    <div className="operations-list-page query-trigger-tasks-page">
      <PageHeader
        title="定时触发任务"
      />

      <section className="query-task-list-surface">
        <header className="operation-list-surface-header">
          <div>
            <h2>任务列表</h2>
            <p>启用的任务会按计划查询 Jira，并对未命中去重窗口的工单触发规则组。</p>
          </div>
        </header>
        <div className="operation-list-surface-body">
        {loading ? (
          <TableSkeleton rowCount={5} columnCount={6} label="加载定时触发任务中" />
        ) : tasks.length === 0 ? (
          <EmptyState title="暂无定时触发任务" description="在操作记录页按条件查询工单后，可保存为定时任务。" />
        ) : (
          <>
          <div className="table-container query-task-table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="table query-task-table">
              <colgroup>
                <col className="query-task-col-scope" />
                <col className="query-task-col-schedule" />
                <col className="query-task-col-run-status" />
                <col className="query-task-col-enabled-status" />
                <col className="query-task-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>任务与查询范围</th>
                  <th>计划</th>
                  <th data-column="run-status">运行状态</th>
                  <th data-column="enabled-status">启用状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(task => (
                  <tr key={task.id} className="query-task-row">
                    <td>
                      <strong>{task.name}</strong>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px' }}>{getGroupName(task.group_id)} · {conditionSummary(task)}</div>
                    </td>
                    <td>{scheduleLabel(task)}<div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>下次 {task.next_run_at || '-'}</div></td>
                    <td>
                      <div style={{ display: 'grid', gap: '4px' }}>
                        {getRunStatusBadge(task.run_status)}
                        {task.run_started_at && (
                          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                            开始于 {task.run_started_at}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{getStatusBadge(task.is_active)}</td>
                    <td>
                      <div className="query-task-actions">
                        <span className="query-task-action-slot">
                          <button
                            type="button"
                            className="table-action-icon-button"
                            onClick={() => handleOpenEditTask(task)}
                            aria-label="编辑定时任务"
                            title="编辑定时任务"
                          >
                            <Pencil size={17} aria-hidden="true" />
                          </button>
                        </span>
                        <span className="query-task-action-slot">
                          <button
                            type="button"
                            className="table-action-icon-button"
                            onClick={() => handleToggleTask(task)}
                            aria-label={task.is_active ? '停用定时任务' : '启用定时任务'}
                            title={task.is_active ? '停用定时任务' : '启用定时任务'}
                          >
                            {task.is_active ? <Pause size={17} aria-hidden="true" /> : <Play size={17} aria-hidden="true" />}
                          </button>
                        </span>
                        <span className="query-task-terminate-slot">
                        {canCancelQueryTriggers && task.run_status === 'running' && (
                          <button
                            type="button"
                            className="table-action-icon-button is-danger"
                            onClick={() => handleCancelRunningTask(task)}
                            disabled={cancellingTaskId === task.id}
                            aria-label="终止当前运行"
                            title="终止当前运行"
                          >
                            <Square size={15} fill="currentColor" aria-hidden="true" />
                          </button>
                        )}
                        {canCancelQueryTriggers && task.run_status === 'cancel_requested' && (
                          <button
                            type="button"
                            className="table-action-icon-button is-danger"
                            disabled
                            aria-label="终止中"
                            title="终止中"
                          >
                            <Square size={15} fill="currentColor" aria-hidden="true" />
                          </button>
                        )}
                        </span>
                        <span className="query-task-action-slot">
                          <button
                            type="button"
                            className="table-action-icon-button is-danger"
                            onClick={() => handleDeleteTask(task)}
                            aria-label="删除定时任务"
                            title="删除定时任务"
                          >
                            <X size={19} aria-hidden="true" />
                          </button>
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="query-task-mobile-list responsive-operation-list">
            {tasks.map(task => (
              <article className="responsive-operation-row" key={task.id}>
                <div className="responsive-operation-main">
                  <strong>{task.name}</strong>
                  <span>{getGroupName(task.group_id)}</span>
                  <small>{conditionSummary(task)}</small>
                </div>
                <div className="query-task-mobile-status">
                  {getRunStatusBadge(task.run_status)}
                  {getStatusBadge(task.is_active)}
                </div>
                <div className="responsive-operation-meta">
                  <span>{scheduleLabel(task)} · 下次 {task.next_run_at || '-'}</span>
                  <div className="query-task-mobile-actions">
                    <button type="button" className="table-action-icon-button" onClick={() => handleOpenEditTask(task)} aria-label="编辑定时任务" title="编辑定时任务">
                      <Pencil size={17} aria-hidden="true" />
                    </button>
                    <button type="button" className="table-action-icon-button" onClick={() => handleToggleTask(task)} aria-label={task.is_active ? '停用定时任务' : '启用定时任务'} title={task.is_active ? '停用定时任务' : '启用定时任务'}>
                      {task.is_active ? <Pause size={17} aria-hidden="true" /> : <Play size={17} aria-hidden="true" />}
                    </button>
                    {canCancelQueryTriggers && task.run_status === 'running' && (
                      <button type="button" className="table-action-icon-button is-danger" onClick={() => handleCancelRunningTask(task)} aria-label="终止当前运行" title="终止当前运行">
                        <Square size={15} fill="currentColor" aria-hidden="true" />
                      </button>
                    )}
                    <button type="button" className="table-action-icon-button is-danger" onClick={() => handleDeleteTask(task)} aria-label="删除定时任务" title="删除定时任务">
                      <X size={19} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
          </>
        )}
        </div>
      </section>

      <Confirm
        isOpen={deleteConfirmState.isOpen}
        title="确认操作"
        message={deleteConfirmState.message}
        onConfirm={deleteConfirmState.onConfirm}
        onCancel={() => setDeleteConfirmState({ isOpen: false, message: '', onConfirm: null })}
      />
      <Confirm
        isOpen={cancelConfirmState.isOpen}
        title="终止当前运行"
        message="已开始的工单会处理完成，之后不再创建或处理新的工单；定时任务保持启用，并会在下次计划时间正常执行。"
        onConfirm={confirmCancelRunningTask}
        onCancel={() => setCancelConfirmState({ isOpen: false, task: null })}
        loading={Boolean(cancellingTaskId)}
        disabled={Boolean(cancellingTaskId)}
        confirmText="确认终止"
        loadingText="终止中..."
      />
      {alertState.isOpen && <Message
        type={alertState.type || 'info'}
        message={alertState.message}
        onClose={() => setAlertState({ ...alertState, isOpen: false })}
      />}

      <Modal
        isOpen={Boolean(editingTask)}
        title="编辑定时任务"
        onClose={handleCloseEditTask}
        size="large"
        buttons={[
          { label: '取消', onClick: handleCloseEditTask, variant: 'default' },
          { label: '保存', onClick: handleSaveEditTask, variant: 'primary' }
        ]}
      >
        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">任务名</label>
              <input className="form-input" value={editForm.name} onChange={(e) => handleEditFormChange('name', e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">规则组</label>
              <SearchableSelect
                value={editForm.group_id}
                options={groups.map(group => ({ value: group.id, label: group.name }))}
                onChange={(val) => handleEditFormChange('group_id', val)}
                placeholder="选择规则组"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">条件关系</label>
              <SearchableSelect
                value={editForm.condition_logic}
                options={[
                  { value: 'and', label: '全部满足' },
                  { value: 'or', label: '任一满足' }
                ]}
                onChange={(val) => handleEditFormChange('condition_logic', val)}
                searchable={false}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">最大数量</label>
              <input className="form-input" type="number" min="1" max="10000" value={editForm.max_results} onChange={(e) => handleEditFormChange('max_results', e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">单页数量</label>
              <input className="form-input" type="number" min="1" max="50" value={editForm.page_size} onChange={(e) => handleEditFormChange('page_size', e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">去重小时</label>
              <input className="form-input" type="number" min="0" value={editForm.dedupe_hours} onChange={(e) => handleEditFormChange('dedupe_hours', e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">状态</label>
              <SearchableSelect
                value={editForm.is_active ? 'active' : 'inactive'}
                options={[
                  { value: 'active', label: '启用' },
                  { value: 'inactive', label: '停用' }
                ]}
                onChange={(val) => handleEditFormChange('is_active', val === 'active')}
                searchable={false}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">频率</label>
              <SearchableSelect
                value={editForm.schedule_type}
                options={[
                  { value: 'hourly', label: '每小时' },
                  { value: 'daily', label: '每天' },
                  { value: 'weekly', label: '每周' }
                ]}
                onChange={(val) => handleEditFormChange('schedule_type', val)}
                searchable={false}
              />
            </div>
            {editForm.schedule_type === 'hourly' ? (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">间隔分钟</label>
                <input className="form-input" type="number" min="1" value={editForm.interval_minutes} onChange={(e) => handleEditFormChange('interval_minutes', e.target.value)} />
              </div>
            ) : (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">时间</label>
                <input className="form-input" type="time" value={editForm.schedule_time} onChange={(e) => handleEditFormChange('schedule_time', e.target.value)} />
              </div>
            )}
            {editForm.schedule_type === 'weekly' && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">星期</label>
                <SearchableSelect
                  value={editForm.week_day}
                  options={weekDayOptions}
                  onChange={(val) => handleEditFormChange('week_day', val)}
                  searchable={false}
                />
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {editConditions.length === 0 ? (
                <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>暂无查询条件</span>
              ) : editConditions.map((condition, index) => (
                <span key={`${condition.field}-${condition.operator}-${index}`} className="badge badge-default" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                  {getFieldName(condition.field)} {getOperatorLabel(condition.operator)} {['is_empty', 'is_not_empty'].includes(condition.operator) ? '' : Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}
                  <button className="btn btn-ghost btn-sm" onClick={() => removeEditCondition(index)} style={{ padding: '2px 6px' }}>×</button>
                </span>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
              <div className="form-group" style={{ flex: 1, minWidth: '180px', marginBottom: 0 }}>
                <label className="form-label">字段</label>
                <SearchableSelect
                  value={newEditCondition.field}
                  options={fields.map(field => ({ value: field.field_id, label: field.display_name || field.field_id }))}
                  onChange={(val) => setNewEditCondition(prev => ({ ...prev, field: val, value: '' }))}
                  placeholder="选择字段"
                />
              </div>
              <div className="form-group" style={{ width: '160px', marginBottom: 0 }}>
                <label className="form-label">操作符</label>
                <SearchableSelect
                  value={newEditCondition.operator}
                  options={queryOperatorOptions}
                  onChange={(val) => setNewEditCondition(prev => ({
                    ...prev,
                    operator: val,
                    value: ['is_empty', 'is_not_empty'].includes(val) ? '' : ['contains', 'not_contains', 'in', 'not_in'].includes(val) && selectedEditFieldOptions.length > 0 ? [] : ''
                  }))}
                  searchable={false}
                />
              </div>
              {editConditionNeedsValue && (
                <div className="form-group" style={{ flex: 1, minWidth: '180px', marginBottom: 0 }}>
                  <label className="form-label">值</label>
                  {selectedEditFieldOptions.length > 0 ? (
                    <SearchableSelect
                      value={newEditCondition.value}
                      options={selectedEditFieldOptions.map(option => ({ value: option.value || option.id || option.name, label: option.label || option.value || option.name }))}
                      onChange={(val) => setNewEditCondition(prev => ({ ...prev, value: val }))}
                      placeholder={editConditionSupportsMultipleValues ? '选择一个或多个值' : '选择值'}
                      multiple={editConditionSupportsMultipleValues}
                    />
                  ) : (
                    <input
                      type="text"
                      value={newEditCondition.value}
                      onChange={(e) => setNewEditCondition(prev => ({ ...prev, value: e.target.value }))}
                      className="form-input"
                      placeholder="输入查询值"
                    />
                  )}
                </div>
              )}
              <button className="btn btn-secondary" onClick={addEditCondition}>添加条件</button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default QueryTriggerTasksPage;
